import { useMemo, useState } from "react";

/**
 * Rows per page before anyone touches the selector, for every /admin table —
 * client-paginated ones inherit it from here, and the server-paginated pages
 * seed their own state with the same number. Ten: the pages that opened on 25
 * or 50 pushed their pagination off the bottom of the screen, and the point of
 * a first page is to be read without scrolling.
 */
const DEFAULT_PAGE_SIZE = 10;

interface IUseMockPaginationOptions<T> {
  data: T[];
  defaultPageSize?: number;
  searchFields?: (keyof T)[];
}

interface IUseMockPaginationReturn<T> {
  items: T[];
  /**
   * Every row the search matches, across all pages. What the CSV export
   * writes: someone exporting a filtered list wants the filter applied, not
   * the twenty-five rows that happen to be on screen.
   */
  allItems: T[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalItems: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  isLoading: false;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

export function useMockPagination<T>({
  data,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  searchFields = [],
}: IUseMockPaginationOptions<T>): IUseMockPaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    if (!searchTerm || searchFields.length === 0) return data;

    const lower = searchTerm.toLowerCase();
    return data.filter((item) =>
      searchFields.some((field) => {
        const value = item[field];
        if (value == null) return false;
        return String(value).toLowerCase().includes(lower);
      })
    );
  }, [data, searchTerm, searchFields]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  const items = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, safePage, pageSize]);

  const setPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setCurrentPage(1);
  };

  return {
    items,
    allItems: filteredData,
    pagination: {
      currentPage: safePage,
      pageSize,
      totalPages,
      totalItems: filteredData.length,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > 1,
    },
    isLoading: false as const,
    searchTerm,
    setSearchTerm: (term: string) => {
      setSearchTerm(term);
      setCurrentPage(1);
    },
    setPage,
    setPageSize,
  };
}
